import { concurrent, map, toAsync, toArray, pipe } from '@fxts/core';
import { VectorAppApiCall } from '../sizeCalculator/api/api.application.call';
import type { GenerateAcrylStandResponse, GenerateNameTagResponse } from '@modules/Vector/S/api.dto';
import { ACRYL_STANDIZER_OPTS, NAME_TAG_OPTS } from '@modules/Vector/S/Constant/acryl-standizer.constant';

interface GenerateOptions {
  concurrency?: number;
  fileKey?: string;
  offsetDistanceMm?: number;
  strokeStyle?: { stroke: string; strokeWidth: number };
  smoothOffsetAdder?: number;
  standDimensionMm?: {
    ground: { height: number; width: number; round: number };
    notch: { height: number; width: number; round: number };
  };
  alphaThreshold?: number;
  blurSigma?: number;
  simplify?: {
    method: 'simplifyjs' | 'paperjs';
    simplifyOptions?: {
      tolerance?: number;
      highQuality?: boolean;
    };
  };
  curvify?: {
    method: 'odiak' | 'paperjs';
    curvifyOptions?: {
      maxError?: number;
    };
  };
}

/**
 * Resolves and merges provided options with default constants.
 */
const resolveGenerateOptions = (options: GenerateOptions, inputLength: number) => ({
  fileKey: options.fileKey || 'maple/acryl-stand/album',
  offsetDistanceMm: options.offsetDistanceMm ?? ACRYL_STANDIZER_OPTS.DEFAULT_OFFSET_MM,
  strokeStyle: options.strokeStyle ?? ACRYL_STANDIZER_OPTS.STROKE_STYLE,
  smoothOffsetAdder: options.smoothOffsetAdder ?? ACRYL_STANDIZER_OPTS.SMOOTH_OFFSET_ADDER,
  standDimensionMm: options.standDimensionMm ?? ACRYL_STANDIZER_OPTS.STAND_DIMENSION_MM,
  concurrency: Math.min(Math.max(1, options.concurrency ?? inputLength), inputLength),
  alphaThreshold: options.alphaThreshold ?? ACRYL_STANDIZER_OPTS.ALPHA_THRESHOLD,
  blurSigma: options.blurSigma ?? ACRYL_STANDIZER_OPTS.BLUR_SIGMA,
  simplify: options.simplify,
  curvify: options.curvify,
});

/**
 * 메이플 캐릭터 이미지를 위한 아크릴 스탠드를 생성합니다.
 */
export class CreateMapleAcrylStand {
  private api: VectorAppApiCall | null;
  private isDestroyed = false;

  constructor() {
    this.api = new VectorAppApiCall();
  }

  /**
   * 주어진 이미지 URL들에 대해 전처리와 아크릴 스탠드 생성을 조율합니다.
   * - 전처리: 메이플 캐릭터 이미지를 자르고 크기를 조정합니다.
   * - 생성: 컷라인과 중앙 정렬된 디자인 이미지를 생성한 후, 아크릴 스탠드를 생성합니다.
   */
  async generate(imageUrls: string[], options: GenerateOptions = {}): Promise<GenerateAcrylStandResponse[]> {
    if (this.isDestroyed || this.api == null) return [];
    if (!Array.isArray(imageUrls) || imageUrls.length === 0) return [];

    const opts = resolveGenerateOptions(options, imageUrls.length);
    const api = this.api;
    if (api == null) return [];

    return pipe(
      imageUrls,
      toAsync,
      map(async (imageUrl) =>
        api.acrylStandImageProcessingMapleCharacter({ image_url: imageUrl, file_key: opts.fileKey }),
      ),
      map(async (preprocessed) =>
        api.generateAcrylStand({
          image_url: preprocessed.url,
          file_key: opts.fileKey,
          offset_distance_mm: opts.offsetDistanceMm,
          stroke_style: opts.strokeStyle,
          smooth_offset_adder: opts.smoothOffsetAdder,
          stand_dimension_mm: opts.standDimensionMm,
          alpha_threshold: opts.alphaThreshold,
          blur_sigma: opts.blurSigma,
          simplify: opts.simplify,
          curvify: opts.curvify,
        }),
      ),
      concurrent(opts.concurrency),
      toArray,
    );
  }

  /**
   * 메이플 캐릭터 아바타의 아크릴 스탠드를 이름표와 함께 생성합니다.
   * - 전처리: 메이플 캐릭터 이미지를 자르고 크기를 조정합니다 (imageUrl만 사용).
   * - 생성: 전처리된 이미지 URL로부터 아크릴 스탠드를 생성합니다.
   * - 이름: name 값은 함께 전달되지만 여기서는 처리되지 않습니다.
   */
  async generateWithNameTag(
    avatars: { imageUrl: string; name: string }[],
    options: GenerateOptions = {},
  ): Promise<{ avater: GenerateAcrylStandResponse; nameTag: GenerateNameTagResponse }[]> {
    if (this.isDestroyed || this.api == null) return [];
    if (!Array.isArray(avatars) || avatars.length === 0) return [];

    const opts = resolveGenerateOptions(options, avatars.length);
    const api = this.api;
    if (api == null) return [];

    return pipe(
      avatars,
      toAsync,
      map(async ({ imageUrl, name }) => {
        const preprocessed = await api.acrylStandImageProcessingMapleCharacter({
          image_url: imageUrl,
          file_key: opts.fileKey,
        });
        return { preprocessed, name };
      }),
      map(async ({ preprocessed, name }) => {
        const [avater, nameTag] = await Promise.all([
          api.generateAcrylStand({
            image_url: preprocessed.url,
            file_key: `${opts.fileKey}/avatar`,
            offset_distance_mm: opts.offsetDistanceMm,
            stroke_style: opts.strokeStyle,
            smooth_offset_adder: opts.smoothOffsetAdder,
            stand_dimension_mm: opts.standDimensionMm,
            alpha_threshold: opts.alphaThreshold,
            blur_sigma: opts.blurSigma,
          }),
          api.generateNameTag({
            text: name,
            file_key: `${opts.fileKey}/name-tag`,
            backgroundColor: NAME_TAG_OPTS.BACKGROUND_COLOR,
            edgeRemovalSize: NAME_TAG_OPTS.EDGE_REMOVAL_SIZE,
            fontColor: NAME_TAG_OPTS.FONT_COLOR,
            fontSize: NAME_TAG_OPTS.FONT_SIZE,
            padding: NAME_TAG_OPTS.PADDING,
            margin: NAME_TAG_OPTS.MARGIN,
            round: NAME_TAG_OPTS.ROUND,
          }),
        ]);
        return { avater, nameTag };
      }),
      concurrent(opts.concurrency),
      toArray,
    );
  }

  destruct(): void {
    this.isDestroyed = true;
    this.api = null;
  }
}
